import * as React from "react"
import { Autocomplete as AutocompletePrimitive } from "@base-ui/react/autocomplete"
import { ChevronDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { Input } from "./form"

/**
 * A text box that suggests, rather than a list that forbids.
 *
 * Base UI's Autocomplete in `mode="list"`: the rows narrow as you type, and a
 * value that appears in no row is still a value. That pair is the whole reason
 * this file exists. Some fields here hold the customer's vocabulary rather than
 * ours — the machine types on a shop floor nobody here has seen — so a Select
 * would refuse a legitimate answer, while a bare text box hides the fact that
 * there is anything to pick from at all.
 *
 * ## Why not `<datalist>`
 *
 * That is what this replaces, and it failed twice for the same underlying
 * reason: the popup is browser UI, not page UI. Chrome draws it in its own dark
 * chrome no matter what the page's theme is, and an `<option>` shows *one* of
 * its value and its label — never both. A list of types annotated "already has
 * an active form" therefore rendered as six identical rows with the type name,
 * the only part anyone needed, nowhere on screen. Moving the annotation from
 * the `label` attribute to the option's text content changes nothing: with no
 * `label`, Chrome uses the text content as the label instead. There is no third
 * arrangement, and no CSS reaches any of it.
 *
 * ## Shape
 *
 * The parts follow ./dropdown-menu: `AutocompleteContent` is the whole
 * Portal → Positioner → Popup stack, so a call site writes a field and some
 * rows and nothing about floating elements.
 */

function Autocomplete<ItemValue>(
  props: Omit<AutocompletePrimitive.Root.Props<ItemValue>, "items"> & {
    items?: readonly ItemValue[] | undefined
  },
) {
  return <AutocompletePrimitive.Root {...props} />
}

/**
 * The box around the input and its caret. Positions the caret; nothing else.
 */
function AutocompleteInputGroup({
  className,
  ...props
}: AutocompletePrimitive.InputGroup.Props) {
  return (
    <AutocompletePrimitive.InputGroup
      data-slot="autocomplete-input-group"
      className={cn("relative", className)}
      {...props}
    />
  )
}

/*
 * The field itself is ./form's `Input`, reached through `render`.
 *
 * Copying its classes here instead would have drifted: `Input` is where the
 * 44px floor and the filled iOS surface live, and it is also what reads this
 * app's `Field` context to pick up the label's id, `aria-describedby` and
 * `aria-invalid`. Composing it keeps one definition of all of that.
 *
 * `id` is dropped on the way through, which is the one thing that has to be
 * deliberate. Base UI generates an id for its input and passes it down; `Input`
 * prefers an explicit id over the one `Field` handed it, so leaving Base UI's
 * in place would point the `<label for>` at an element that no longer carries
 * that id — a label that does nothing when tapped and a control with no
 * accessible name. Nothing inside Base UI reads the input's id back, so
 * dropping it costs nothing.
 */
function AutocompleteInput({
  className,
  ...props
}: AutocompletePrimitive.Input.Props) {
  return (
    <AutocompletePrimitive.Input
      data-slot="autocomplete-input"
      // Room for the caret, so a long value does not run underneath it.
      className={cn("pr-12", className)}
      {...props}
      render={({ id: _baseUiId, ...inputProps }) => <Input {...inputProps} />}
    />
  )
}

/**
 * The caret that opens the list.
 *
 * Sized and placed like the reveal toggle in ./form — `size-control`, because
 * this is a control a gloved thumb has to hit and 44px is the floor this app
 * holds itself to on touch (36px with a mouse, like the field it sits in). Base UI keeps it out of the tab order on its own: it does nothing
 * that <kbd>ArrowDown</kbd> on the input does not already do, so a second tab
 * stop would be noise.
 */
function AutocompleteTrigger({
  className,
  children,
  ...props
}: AutocompletePrimitive.Trigger.Props) {
  return (
    <AutocompletePrimitive.Trigger
      data-slot="autocomplete-trigger"
      className={cn(
        "group/autocomplete-trigger text-muted-foreground hover:text-foreground absolute top-1/2 right-1 -translate-y-1/2",
        "flex size-control items-center justify-center rounded-md transition-colors",
        "focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children ?? (
        <ChevronDown
          className="size-4.5 transition-transform duration-150 group-data-[popup-open]/autocomplete-trigger:rotate-180"
          aria-hidden
        />
      )}
    </AutocompletePrimitive.Trigger>
  )
}

/*
 * A list is as wide as its rows, not as wide as the field that opened it.
 *
 * ./dropdown-menu records what `w-(--anchor-width)` costs — rows laid out in
 * the trigger's width and clipped — and the temptation is stronger here,
 * because a popup hanging off a text field *looks* like it should line up with
 * it. It does line up: the anchor width is the floor, so the list is never
 * narrower than the field. `w-max` then lets a row that carries a long
 * annotation take the room it needs, and `max-w-(--available-width)` stops it
 * before the edge of the screen.
 */
function AutocompleteContent({
  align = "start",
  alignOffset = 0,
  side = "bottom",
  sideOffset = 4,
  className,
  ...props
}: AutocompletePrimitive.Popup.Props &
  Pick<
    AutocompletePrimitive.Positioner.Props,
    "align" | "alignOffset" | "side" | "sideOffset"
  >) {
  return (
    <AutocompletePrimitive.Portal>
      <AutocompletePrimitive.Positioner
        className="isolate z-50 outline-none"
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
      >
        <AutocompletePrimitive.Popup
          data-slot="autocomplete-content"
          className={cn("z-50 max-h-(--available-height) max-w-(--available-width) w-max min-w-[max(12rem,var(--anchor-width))] origin-(--transform-origin) overflow-x-hidden overflow-y-auto overscroll-contain rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10 duration-100 outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:overflow-hidden data-closed:fade-out-0 data-closed:zoom-out-95", className )}
          {...props}
        />
      </AutocompletePrimitive.Positioner>
    </AutocompletePrimitive.Portal>
  )
}

function AutocompleteList({
  className,
  ...props
}: AutocompletePrimitive.List.Props) {
  return (
    <AutocompletePrimitive.List
      data-slot="autocomplete-list"
      className={cn("flex flex-col", className)}
      {...props}
    />
  )
}

/**
 * One row.
 *
 * `data-highlighted` rather than `focus:`, which is where this differs from
 * ./dropdown-menu: a menu moves real focus onto the item it opens on, while a
 * combobox leaves focus in the input and marks the active row with
 * `aria-activedescendant`. A `focus:` rule here would never match anything.
 *
 * `min-h-control` for the same reason the caret is `size-control`. The row is a column so
 * that an annotation can sit under the name rather than beside it; see
 * `AutocompleteItemNote`.
 */
function AutocompleteItem({
  className,
  ...props
}: AutocompletePrimitive.Item.Props) {
  return (
    <AutocompletePrimitive.Item
      data-slot="autocomplete-item"
      className={cn(
        "relative flex min-h-control cursor-default flex-col justify-center gap-0.5 rounded-md px-2.5 py-1.5 pointer-fine:py-1 text-sm outline-hidden select-none data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50",
        className,
      )}
      {...props}
    />
  )
}

/**
 * The second line of a row: what is worth knowing about this choice, under the
 * name of the choice itself.
 *
 * Deliberately not restyled when the row is highlighted. `muted-foreground`
 * clears `accent` in both themes — the two were moved apart on purpose, see the
 * notes in index.css — so the annotation keeps reading as subordinate instead
 * of jumping to the same weight as the name the moment the row lights up.
 */
function AutocompleteItemNote({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="autocomplete-item-note"
      className={cn("text-muted-foreground text-xs leading-snug", className)}
      {...props}
    />
  )
}

/**
 * Shown in place of the rows when the query matches none of them.
 *
 * Base UI keeps this element mounted whether or not it has anything to say, so
 * that a screen reader announces the change; `empty:p-0` is what collapses it
 * the rest of the time. Hiding it with `display: none` would silence that
 * announcement, which is the one thing Base UI's own note about this asks you
 * not to do.
 */
function AutocompleteEmpty({
  className,
  ...props
}: AutocompletePrimitive.Empty.Props) {
  return (
    <AutocompletePrimitive.Empty
      data-slot="autocomplete-empty"
      className={cn(
        "text-muted-foreground px-2.5 py-3 text-sm leading-snug empty:p-0",
        className,
      )}
      {...props}
    />
  )
}

export {
  Autocomplete,
  AutocompleteInputGroup,
  AutocompleteInput,
  AutocompleteTrigger,
  AutocompleteContent,
  AutocompleteList,
  AutocompleteItem,
  AutocompleteItemNote,
  AutocompleteEmpty,
}
